import {BaseTexture, Container, Graphics, NineSlicePlane, Sprite, Text, TextStyle, Texture} from "pixi.js";
import {tsthreeConfig} from "../../config/tsthreeConfig";
import {GAME_FONT, GAME_FONT_BOLD} from "../Constants/Constants";
import {buttonify} from "../../engine/HelperFunctions/buttonify";
import {DEFAULT_TEXTURE_B64} from "../../engine/Constants/Constants";
import {GameObject, HelperFunctions, IVector2} from "../../tsthree";
import {bringToFront} from "../../engine/HelperFunctions/bringToFront";
import { IWindowState } from "../Types/IWindowState";
import { TutorialStageComponent } from "../Components/TutorialStageComponent";

export const WINDOW_CONSTANTS = {
    headerHeight: 18,
};

export interface IWindowSettings {
    /**
     * Creates the window without any premade elements
     */
    createEmpty?: boolean;
    windowStateRef: IWindowState<Windows95Window>;
    // skipFullscreenButton?: boolean;
    title: string;
    iconKey?: string;

    dimensions?: {
        width: number;
        height: number;
    }

    onMinimise: () => void;
    onClose: () => void;
}

export class Windows95Window extends GameObject {

    protected _windowStateRef: IWindowState<Windows95Window>;

    public get windowName(): string {
        return this._title?.text || "";
    }

    public set windowName(_str: string) {
        if(this._title)
            this._title.text = _str;
    }

    public get icon(): Texture {
        return this._icon?.texture || null;
    }

    public set icon(_val: Texture) {
        this._icon.texture = _val;
    }

    protected _hasContext: boolean = false;
    public get hasContext(): boolean { return this._hasContext; }
    public set hasContext(val: boolean) { this._hasContext = val; }

    protected _onClose: () => void;
    protected contentContainer: Container;
    protected _icon: Sprite;
    protected _title: Text;
    public headerBg: Graphics;
    public headerBgGrey: Graphics;

    protected _fullscreen: boolean = false;

    protected _closeButton: Sprite;
    protected _fullscreenButton: Sprite;
    protected _minimiseButton: Sprite;
    protected _windowBg: NineSlicePlane;
    protected _cachedDimensions: {
        width: number;
        height: number;
    } = {
        width: 0,
        height: 0
    };

    private makeHeaderBg(isGrey: boolean = false, dimensions?: {width: number}): Graphics {
        const headerBgGraphics = new Graphics();
        headerBgGraphics.beginFill(
            isGrey ? 0x909090 : 0x0000aa,
            1
        );

        if(dimensions) {
            headerBgGraphics.drawRect(0, 0, dimensions.width - 4, WINDOW_CONSTANTS.headerHeight);
            // headerBgGraphics.position.set(2, 2);
        } else {
            headerBgGraphics.drawRect(0, 0, tsthreeConfig.width, WINDOW_CONSTANTS.headerHeight);
        }

        headerBgGraphics.endFill();

        if(dimensions) {
            let startPos: IVector2 = null;
            let startInputPos: IVector2 = null;
            buttonify(headerBgGraphics, {
                disableButtonMode: true,
                trackMovementOutsideElement: true,
                onFire: () => {},
                onPointerDown: (e: any) => {
                    e = HelperFunctions.parseInteractionEvent(e);
                    startPos = {x: this.position.x, y: this.position.y};
                    startInputPos = {x: e.x, y: e.y};
                    bringToFront(this);
                    this._windowStateRef.selected = true;
                    this._windowStateRef.dirty = true;
                },
                onPointerUp: () => (startPos = startInputPos = null),
                onPointerMove: (ev) => {
                    if(startPos && this._windowStateRef.selected) {
                        const newPos = HelperFunctions.parseInteractionEvent(ev);
                        this.position.set(
                            startPos.x - (startInputPos.x - newPos.x),
                            startPos.y - (startInputPos.y - newPos.y),
                        );
                    } else if(!this._windowStateRef.selected) startPos = null;
                },
            });
        } else {
            buttonify(headerBgGraphics, {
                disableButtonMode: true,
                trackMovementOutsideElement: true,
                onFire: () => {},
                onPointerDown: (e: any) => {
                    bringToFront(this);
                    this._windowStateRef.selected = true;
                    this._windowStateRef.dirty = true;
                },
            });
        }

        return headerBgGraphics;
    }

    constructor(_settings: IWindowSettings) {
        super();
        this.name = _settings.title;
        this.interactive = true;
        this.interactiveChildren = true;
        this._windowStateRef = _settings.windowStateRef;
        this._onClose = _settings.onClose || (() => {});

        this._fullscreen = !_settings.dimensions;
        if(_settings.dimensions) {
            this._cachedDimensions.width = _settings.dimensions.width;
            this._cachedDimensions.height = _settings.dimensions.height;
        } else {
            this._cachedDimensions.width = tsthreeConfig.width;
            this._cachedDimensions.height = tsthreeConfig.height;
        }

        if(!_settings.createEmpty) {
            const headerContainer = new Container();
            if(_settings.dimensions) {
                headerContainer.position.set(2, 2);
            }
            const headerBg =
                this.headerBg =
                    this.makeHeaderBg(false, _settings.dimensions);
            const headerBgGrey =
                this.headerBgGrey =
                    this.makeHeaderBg(true, _settings.dimensions);
            this.addChild(headerContainer);
            headerContainer.addChild(headerBg);
            headerContainer.addChild(headerBgGrey);

            const closeButton = this._closeButton = new Sprite(
                ENGINE.getPIXIResource("close-icon") as Texture
            );
            closeButton.anchor.set(1, 0);
            if(_settings.dimensions) {
                closeButton.position.set(_settings.dimensions.width - 6, 2);
            } else {
                closeButton.position.set(tsthreeConfig.width - 2, 2);
            }
            headerContainer.addChild(closeButton);
            buttonify(closeButton, {
                onFire: (ev) => {
                    // this.parent.removeChild(this);
                    this.visible = false;
                    this._onClose();
                }
            });

            let fullscreenButtonWidth = 0;

            const minimiseButton = this._minimiseButton = new Sprite(
                ENGINE.getPIXIResource("minimise-icon") as Texture
            );
            minimiseButton.anchor.set(1, 0);
            if(_settings.dimensions) {
                minimiseButton.position.set(
                    _settings.dimensions.width - 6 - 2 - closeButton.width - fullscreenButtonWidth,
                    2
                );
            } else {
                minimiseButton.position.set(
                    tsthreeConfig.width - 2 - 2 - closeButton.width - fullscreenButtonWidth,
                    2
                );
            }
            buttonify(minimiseButton, {
                onFire: (ev) => {
                    // HACK
                    const test = TutorialStageComponent.instances
                        .find(
                            (e) => e.tutorialStageId === "SelectACaseStage"
                        );
                    if(
                        test?.isActive
                    ) return;
                    // HACK
                    this.visible = false;
                    _settings.onMinimise();
                }
            });
            headerContainer.addChild(minimiseButton);

            const title = this._title = new Text(_settings.title || "Window", new TextStyle({
                fontFamily: GAME_FONT_BOLD,
                fontSize: 11,
                fill: 0xdddddd,
                align: "left"
            }));
            title.anchor.set(0, 0);
            title.position.set(2 + 16 + 3, 2);
            headerContainer.addChild(title);

            const windowIcon = this._icon = new Sprite(
                _settings.iconKey ? ENGINE.getPIXIResource(
                    _settings.iconKey
                ) as Texture : new Texture(new BaseTexture(DEFAULT_TEXTURE_B64))
            );
            windowIcon.width = windowIcon.height = 16;
            windowIcon.position.set(2, 1);
            headerContainer.addChild(windowIcon);

            // const windowBg = new Graphics();
            // windowBg.beginFill(0xc3c7cb, 1);
            const windowBg = this._windowBg = new NineSlicePlane(ENGINE.getPIXIResource("button") as Texture);
            windowBg.width = _settings.dimensions?.width || tsthreeConfig.width;
            windowBg.height = (_settings.dimensions?.height || tsthreeConfig.height) -
                WINDOW_CONSTANTS.headerHeight -
                (!_settings.dimensions ? (ENGINE.getPIXIResource("taskbar-nineslice") as Texture).height : 0);
            // windowBg.drawRect(0, 0, _settings.dimensions?.width || tsthreeConfig.width,
            //     (_settings.dimensions?.height || tsthreeConfig.height) -
            //     WINDOW_CONSTANTS.headerHeight -
            //     (ENGINE.getPIXIResource("taskbar-nineslice") as Texture).height
            // );
            // windowBg.endFill();
            windowBg.position.set(
                0, _settings.dimensions ? 0 : WINDOW_CONSTANTS.headerHeight
            );
            this.addChild(windowBg);

            bringToFront(headerContainer);
        }

        const windowContents = this.contentContainer = new Container();
        windowContents.position.set(0, WINDOW_CONSTANTS.headerHeight);
        this.addChild(windowContents);
    }

    protected resize(_newDimensions: IVector2): void {
        // this.removeChild(this._headerBg);
        // const children = [...this._headerBg.children];
        // this._headerBg.destroy({
        //     children: false
        // });
        // this._headerBg = this.makeHeaderBg(false, { width: _newDimensions.x });
        // this._headerBg.addChild(...children);
        // this.addChild(this._headerBg);
        this.headerBg.width = _newDimensions.x - 5;

        this._closeButton.position.set(_newDimensions.x - 6, 2);
        // this._fullscreenButton.position.set(_newDimensions.x - 6 - 2 - this._closeButton.width, 2);
        this._minimiseButton.position.set(
            _newDimensions.x - 6 - 2 - this._closeButton.width,
            2
        );
        this._windowBg.width = _newDimensions.x;
        this._windowBg.height = (_newDimensions.y) +
            WINDOW_CONSTANTS.headerHeight + 4;
    }
}